import { i18n } from "@tellescope/i18n";
import {
  build_content_link_string,
  build_file_link_string,
  build_form_group_link_string,
  build_form_link_string,
  build_link_string,
  build_portal_link_string,
} from "@tellescope/utilities";

import {
  BRAND_FIELDS,
  CALENDAR_EVENT_FIELDS,
  ENDUSER_FIELDS,
  LEGACY_NAME_TOKENS,
  ORGANIZATION_FIELDS,
  SENDER_FIELDS,
  SIGNATURE_TOKENS,
  STANDALONE_TOKENS,
  type NamespaceField,
} from "./constants";
import type { TokenDataSource, TokenDefinition, TokenGroupId } from "./types";

/** Sentinel display text that renders a plain `{url}[…]` link as a clean bare URL (SMS-safe). */
const LINK_ONLY = "$LINK_ONLY";

/**
 * Display text for a *resource* link (form/file/portal/etc.): the resolver falls back to the
 * generated URL when this is empty, so no sentinel is needed — pass the collected text as-is.
 */
const resourceDisplayName = (params: Record<string, string> | undefined): string => arg(params, "text");

/**
 * The declarative token registry — the single source of truth for token *structure*. Both the
 * insertion palette and load-time recognition derive from this array. A token absent here is still
 * preserved on load as a graceful fallback chip (see `recognize`), so this list need not be
 * exhaustive of every token the resolver supports — only of the ones we give first-class UI to.
 *
 * Label getters wrap `i18n.t("literal", …)` inline (never behind a helper) so the i18next
 * extractor can see each literal and not strip it under `removeUnusedKeys`.
 */

/** Read a collected param, defaulting to an empty string when absent. */
const arg = (params: Record<string, string> | undefined, key: string): string => params?.[key] ?? "";

/**
 * Optional display-text param shared by the link collectors: when left blank the token renders as a
 * bare link (the resolver uses the generated URL, or `$LINK_ONLY` for the plain `{url}[…]` token).
 */
const LINK_TEXT_PARAM = {
  key: "text",
  kind: "displayText",
  required: false,
  getLabel: () => i18n.t("Display text", { ns: "templates" }),
} as const;

/** Display-text param for placeholders, where the text is the placeholder's purpose and stays required. */
const REQUIRED_TEXT_PARAM = {
  key: "text",
  kind: "displayText",
  required: true,
  getLabel: () => i18n.t("Display text", { ns: "templates" }),
} as const;

/** Build value tokens (`{{namespace.field}}`) from a namespace catalog. */
const valueTokens = (namespace: string, groupId: TokenGroupId, fields: NamespaceField[]): TokenDefinition[] =>
  fields.map(({ field, getLabel }) => ({
    id: `${namespace}.${field}`,
    namespace,
    groupId,
    kind: "value",
    getLabel,
    build: () => `{{${namespace}.${field}}}`,
  }));

/** Build whole-token values (`{{TOKEN}}`, no namespace dot) — used by signatures and standalones. */
const wholeTokens = (namespace: string, groupId: TokenGroupId, fields: NamespaceField[]): TokenDefinition[] =>
  fields.map(({ field, getLabel }) => ({
    id: field,
    namespace,
    groupId,
    kind: "value",
    getLabel,
    build: () => `{{${field}}}`,
  }));

/**
 * A `.link` token bound to a backend resource (e.g. `{{forms.<id>.link:<text>}}`), built via the
 * matching `@tellescope/utilities` builder. Empty display text emits `…link:}}`, which the resolver
 * renders using the generated URL.
 */
const resourceLinkToken = (
  namespace: string,
  dataSource: TokenDataSource,
  buildString: (d: { id: string; displayName: string }) => string,
  getLabel: () => string
): TokenDefinition => ({
  id: `${namespace}.link`,
  namespace,
  groupId: "links",
  kind: "link",
  emitsLinkSyntax: true,
  dataSource,
  getLabel,
  params: [
    { key: "id", kind: "resourceId", required: true, getLabel: () => i18n.t("Resource", { ns: "templates" }) },
    LINK_TEXT_PARAM,
  ],
  build: (params) => buildString({ id: arg(params, "id"), displayName: resourceDisplayName(params) }),
});

/**
 * A calendar-event link token with only display text, e.g. `{{calendar_event.ics_link:<text>}}`. No
 * dedicated `@tellescope/utilities` builder exists; empty display text renders as the generated URL.
 */
const calendarLinkToken = (field: string, getLabel: () => string): TokenDefinition => ({
  id: `calendar_event.${field}`,
  namespace: "calendar_event",
  groupId: "calendarEvent",
  kind: "link",
  emitsLinkSyntax: true,
  getLabel,
  params: [LINK_TEXT_PARAM],
  build: (params) => `{{calendar_event.${field}:${resourceDisplayName(params)}}}`,
});

/** A fill-before-send placeholder, in plain (`:`) or link (`.link:`) form. */
const placeholderToken = (isLink: boolean, getLabel: () => string): TokenDefinition => ({
  id: isLink ? "placeholder.link" : "placeholder",
  namespace: "placeholder",
  groupId: "placeholder",
  kind: "placeholder",
  emitsLinkSyntax: isLink,
  getLabel,
  params: [
    { key: "id", kind: "text", required: true, getLabel: () => i18n.t("Placeholder name", { ns: "templates" }) },
    REQUIRED_TEXT_PARAM,
  ],
  build: (params) => `{{placeholder.${arg(params, "id")}${isLink ? ".link" : ""}:${arg(params, "text")}}}`,
});

export const TOKEN_DEFINITIONS: TokenDefinition[] = [
  ...valueTokens("enduser", "patient", ENDUSER_FIELDS),
  ...valueTokens("relatedcontact", "relatedContact", ENDUSER_FIELDS),
  ...valueTokens("sender", "sender", SENDER_FIELDS),
  ...valueTokens("organization", "organization", ORGANIZATION_FIELDS),
  ...valueTokens("brand", "brand", BRAND_FIELDS),
  ...valueTokens("calendar_event", "calendarEvent", CALENDAR_EVENT_FIELDS),
  ...wholeTokens("signature", "signature", SIGNATURE_TOKENS),
  ...wholeTokens("standalone", "other", STANDALONE_TOKENS),
  ...LEGACY_NAME_TOKENS.map(
    ({ token, getLabel }): TokenDefinition => ({
      id: token,
      namespace: "legacy",
      groupId: "other",
      kind: "value",
      getLabel,
      build: () => token,
    })
  ),
  {
    id: "link",
    namespace: "link",
    groupId: "links",
    kind: "link",
    emitsLinkSyntax: true,
    getLabel: () => i18n.t("Link", { ns: "templates" }),
    params: [
      { key: "url", kind: "url", required: true, getLabel: () => i18n.t("URL", { ns: "templates" }) },
      LINK_TEXT_PARAM,
    ],
    // Plain link: empty display text uses the $LINK_ONLY sentinel so it renders as a clean bare URL.
    build: (params) => build_link_string({ url: arg(params, "url"), displayName: arg(params, "text") || LINK_ONLY }),
  },
  resourceLinkToken("forms", "forms", build_form_link_string, () => i18n.t("Form Link", { ns: "templates" })),
  resourceLinkToken("form_groups", "form_groups", build_form_group_link_string, () =>
    i18n.t("Form Group Link", { ns: "templates" })
  ),
  resourceLinkToken("files", "files", build_file_link_string, () => i18n.t("File Link", { ns: "templates" })),
  resourceLinkToken("content", "content", build_content_link_string, () => i18n.t("Content Link", { ns: "templates" })),
  {
    id: "portal.link",
    namespace: "portal",
    groupId: "links",
    kind: "link",
    emitsLinkSyntax: true,
    getLabel: () => i18n.t("Portal Link", { ns: "templates" }),
    params: [
      {
        key: "destination",
        kind: "portalDestination",
        required: true,
        getLabel: () => i18n.t("Portal page", { ns: "templates" }),
      },
      LINK_TEXT_PARAM,
    ],
    build: (params) =>
      build_portal_link_string({ page: arg(params, "destination"), displayName: resourceDisplayName(params) }),
  },
  calendarLinkToken("portal_link", () => i18n.t("Appointment Portal Link", { ns: "templates" })),
  calendarLinkToken("portal_link_embedded", () => i18n.t("Appointment Portal Link (embedded)", { ns: "templates" })),
  calendarLinkToken("ics_link", () => i18n.t("Appointment Calendar (.ics) Link", { ns: "templates" })),
  {
    id: "snippet",
    namespace: "snippet",
    groupId: "snippet",
    kind: "value",
    dataSource: "snippets",
    getLabel: () => i18n.t("Snippet", { ns: "templates" }),
    params: [
      { key: "key", kind: "resourceId", required: true, getLabel: () => i18n.t("Snippet", { ns: "templates" }) },
    ],
    build: (params) => `{{snippet:${arg(params, "key")}}}`,
  },
  placeholderToken(false, () => i18n.t("Placeholder", { ns: "templates" })),
  placeholderToken(true, () => i18n.t("Placeholder Link", { ns: "templates" })),
];

/** Look up a token definition by its stable id. */
export const getTokenDefinition = (id: string): TokenDefinition | undefined =>
  TOKEN_DEFINITIONS.find((def) => def.id === id);
